import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useFilters } from '../contexts/FilterContext';
import useReport from '../lib/useReport';
import { money, int } from '../lib/format';
import { Card, KpiCard, DataTable, Spinner, ErrorBox, Empty } from '../components/ui';
import PeriodPicker from '../components/PeriodPicker';
import { periodContaining, shiftPeriod } from '../lib/payPeriods';
import { flattenEntries } from '../lib/exports/shared';
import { downloadText } from '../lib/exports/download';
import { buildJournalIif } from '../lib/exports/iif';
import { buildQboJournalCsv } from '../lib/exports/qboJournal';
import { buildJournalCsv, buildJournalSummaryCsv } from '../lib/exports/journalCsv';

// Sales Journal — the POS "Detailed Sales Report" first page as the
// accountant needs it: one balanced journal entry per store per period,
// shown as Money in | Where it went, exportable to QuickBooks Desktop (IIF),
// QuickBooks Online (CSV) or generic CSV. Period basis is paid time with the
// 4 AM business day. See docs/accounting.md.

const FORMATS = [
  { id: 'iif', label: 'QuickBooks Desktop (IIF)', file: 'iif', mime: 'text/plain;charset=utf-8', qb: true },
  { id: 'qbo', label: 'QuickBooks Online (CSV)', file: 'csv', mime: 'text/csv;charset=utf-8', qb: true },
  { id: 'journal', label: 'Journal CSV (generic)', file: 'csv', mime: 'text/csv;charset=utf-8' },
  { id: 'summary', label: 'Summary CSV (one row per store)', file: 'csv', mime: 'text/csv;charset=utf-8' },
];
const formatFor = (software) => (software === 'qbd' ? 'iif' : software === 'qbo' ? 'qbo' : 'journal');

// Colour by what a line IS, so the ledger reads at a glance.
const KIND_CLASS = {
  tender: 'k-tender', ar: 'k-tender', housePayment: 'k-tender', storeCredit: 'k-liab',
  sales: 'k-sales', delivery: 'k-sales', afterTaxCharge: 'k-sales',
  tax1: 'k-tax', tax2: 'k-tax', tax3: 'k-tax',
  tips: 'k-liab', giftcard: 'k-liab', giftcardBonus: 'k-liab', arPayment: 'k-liab', refundTender: 'k-liab', costPaid: 'k-liab',
  refund: 'k-bad', discount: 'k-bad', giftCardBonus: 'k-bad', cost: 'k-bad',
  overShort: 'k-warn',
};

// Sum lines across several entries (all stores, or all days) by identity.
function mergeLines(entries) {
  const map = new Map();
  for (const e of entries) {
    for (const l of e.lines) {
      const key = `${l.side}|${l.kind}|${l.label}|${l.account}`;
      const cur = map.get(key) || { ...l, amount: 0, payments: 0 };
      cur.amount += l.amount;
      cur.payments = (cur.payments || 0) + (l.payments || 0);
      cur.mapped = cur.mapped && l.mapped;
      map.set(key, cur);
    }
  }
  return [...map.values()];
}

function Ledger({ lines, title, sub, tour, badge }) {
  const side = (s) => lines.filter(l => l.side === s && l.amount !== 0);
  const debits = side('debit');
  const credits = side('credit');
  const total = (ls) => ls.reduce((a, l) => a + l.amount, 0);
  const col = (heading, ls, hint) => (
    <div className="ledger-col">
      <h4>{heading} <span className="hint">{hint}</span></h4>
      {ls.length === 0 && <div className="hint">nothing</div>}
      {ls.map((l, i) => (
        <div className={`ledger-row ${KIND_CLASS[l.kind] || ''} ${l.mapped === false ? 'unmapped' : ''}`} key={i}>
          <div>
            <div className="lbl">{l.label}{l.mapped === false && <span className="pill bad">unmapped</span>}{l.payments ? <span className="hint"> · {int(l.payments)} pmts</span> : null}</div>
            <div className="acct">{l.account}</div>
          </div>
          <div className="amt">{money(l.amount)}</div>
        </div>
      ))}
      <div className="ledger-row ledger-total"><div>Total</div><div className="amt">{money(total(ls))}</div></div>
    </div>
  );
  return (
    <Card title={title} sub={sub} tour={tour} actions={badge}>
      <div className="ledger">
        {col('Money in', debits, 'debits')}
        {col('Where it went', credits, 'credits')}
      </div>
    </Card>
  );
}

// Online orders settle through a different merchant account and gateway
// than the in-store terminals: the accountant reconciles each separately.
const CHANNEL_LABEL = { online: 'Online orders', instore: 'In-store orders' };
const emptyChannel = () => ({ orders: 0, netSales: 0, taxes: 0, tips: 0, gross: 0, tenders: {} });
function sumChannels(entries) {
  const out = { online: emptyChannel(), instore: emptyChannel() };
  for (const e of entries) {
    for (const [k, ch] of Object.entries(e.channels || {})) {
      const o = out[k] || (out[k] = emptyChannel());
      o.orders += ch.orders; o.netSales += ch.netSales; o.taxes += ch.taxes; o.tips += ch.tips; o.gross += ch.gross;
      for (const t of ch.tenders || []) {
        const cur = o.tenders[t.tender] || { tender: t.tender, amount: 0, tips: 0, payments: 0 };
        cur.amount += t.amount; cur.tips += t.tips; cur.payments += t.payments;
        o.tenders[t.tender] = cur;
      }
    }
  }
  return out;
}

function ChannelCard({ entries }) {
  const ch = useMemo(() => sumChannels(entries), [entries]);
  const keys = ['online', 'instore', ...Object.keys(ch).filter(k => !CHANNEL_LABEL[k])];
  const total = keys.reduce((a, k) => ({ orders: a.orders + ch[k].orders, netSales: a.netSales + ch[k].netSales, taxes: a.taxes + ch[k].taxes, tips: a.tips + ch[k].tips, gross: a.gross + ch[k].gross }), { orders: 0, netSales: 0, taxes: 0, tips: 0, gross: 0 });
  const rows = [...keys.map(k => ({ key: k, label: CHANNEL_LABEL[k] || k, ...ch[k] })), { key: 'total', label: 'Total', ...total, isTotal: true }];
  return (
    <Card title="By channel" sub="online orders settle through the online gateway’s merchant account; in-store orders through the terminals — tips and taken amounts per channel for reconciling each deposit" tour="sj-channels">
      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th className="no-sort">Channel</th>
              <th className="no-sort num">Orders</th>
              <th className="no-sort num">Net sales</th>
              <th className="no-sort num">Taxes</th>
              <th className="no-sort num">Tips</th>
              <th className="no-sort num">Taken (incl. tax & tips)</th>
              <th className="no-sort">Tenders</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.key} style={r.isTotal ? { fontWeight: 700 } : undefined}>
                <td>{r.label}</td>
                <td className="num">{int(r.orders)}</td>
                <td className="num">{money(r.netSales)}</td>
                <td className="num">{money(r.taxes)}</td>
                <td className="num">{money(r.tips)}</td>
                <td className="num">{money(r.gross + r.tips)}</td>
                <td className="hint">
                  {r.isTotal ? '' : Object.values(r.tenders).sort((a, b) => (b.amount + b.tips) - (a.amount + a.tips))
                    .map(t => `${t.tender} ${money(t.amount + t.tips)}`).join(' · ')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

const BalanceBadge = ({ overShort }) => (
  overShort === 0
    ? <span className="pill good">✓ Balanced</span>
    : <span className="pill warn">{overShort > 0 ? 'Over' : 'Short'} {money(Math.abs(overShort))} — booked to over/short</span>
);

export default function SalesJournalPage() {
  const { anchor, selectedLocations, locations } = useFilters();
  const [range, setRange] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('sd-acct-period') || 'null');
      if (saved?.start && saved?.end) return saved;
    } catch { /* ignore */ }
    // Books get closed for finished months: default to the last complete one.
    const p = shiftPeriod(periodContaining(anchor, { type: 'monthly' }), -1);
    return { start: p.start, end: p.end };
  });
  useEffect(() => { localStorage.setItem('sd-acct-period', JSON.stringify(range)); }, [range]);

  const [granularity, setGranularity] = useState('month');
  const granTouched = useRef(false);
  const [format, setFormat] = useState(null);
  const [storeId, setStoreId] = useState('all');
  const [entryIdx, setEntryIdx] = useState(0);

  const params = useMemo(() => ({
    startDate: range.start, endDate: range.end, granularity,
    ...(selectedLocations.length > 0 ? { locations: selectedLocations.join(',') } : {}),
  }), [range, granularity, selectedLocations]);
  const { data, loading, error } = useReport('accounting/sales-journal', params);

  // Defaults from the (first) store's profile, until the viewer chooses.
  useEffect(() => {
    const p = data?.stores?.[0]?.profile;
    if (!p) return;
    if (!granTouched.current && p.granularity && p.granularity !== granularity) setGranularity(p.granularity);
    if (format === null) setFormat(formatFor(p.software));
  }, [data]);

  const stores = data?.stores || [];
  // One store in view → show its journal directly; "All stores" only exists
  // as a merged read-out when there are several.
  const store = storeId === 'all'
    ? (stores.length === 1 ? stores[0] : null)
    : stores.find(s => String(s.locationId) === String(storeId)) || (stores.length === 1 ? stores[0] : null);
  useEffect(() => { setEntryIdx(0); }, [storeId, granularity, range]);

  // What the ledger shows: one store's entry (or merged days), or every store merged.
  const view = useMemo(() => {
    if (!data) return null;
    if (store) {
      const entries = store.entries || [];
      if (entries.length === 0) return { lines: [], entries: [], overShort: 0, recon: null, dispatch: null };
      // Per business day the Day picker chooses one entry; per month (one entry per store) there is
      // nothing to pick. The condition used to read `granularity === 'month'`, so with daily entries
      // the picker rendered but the ledger always merged every day of the period.
      const single = granularity === 'day' || entries.length === 1 ? entries[Math.min(entryIdx, entries.length - 1)] : null;
      const lines = single ? single.lines : mergeLines(entries);
      const overShort = single ? single.overShort : entries.reduce((a, e) => a + e.overShort, 0);
      const recon = (single ? [single] : entries).reduce((a, e) => ({
        orders: a.orders + (e.orders || 0), voidedLines: a.voidedLines + (e.recon?.voidedLines || 0), voidedAmount: a.voidedAmount + (e.recon?.voidedAmount || 0),
        failedOrders: a.failedOrders + (e.recon?.failedOrders || 0), failedAmount: a.failedAmount + (e.recon?.failedAmount || 0),
        refunds: a.refunds + (e.recon?.refunds || 0), giftCardLines: a.giftCardLines + (e.recon?.giftCardLines || 0), houseCharges: a.houseCharges + (e.recon?.houseCharges || 0),
      }), { orders: 0, voidedLines: 0, voidedAmount: 0, failedOrders: 0, failedAmount: 0, refunds: 0, giftCardLines: 0, houseCharges: 0 });
      const dispatch = (single ? [single] : entries).reduce((a, e) => e.dispatch ? { orders: a.orders + e.dispatch.orders, fee: a.fee + e.dispatch.fee, feeTax: a.feeTax + e.dispatch.feeTax, tip: a.tip + e.dispatch.tip } : a, { orders: 0, fee: 0, feeTax: 0, tip: 0 });
      return { lines, entries, single, overShort, recon, dispatch: dispatch.orders ? dispatch : null };
    }
    const all = flattenEntries(data);
    return {
      lines: mergeLines(all), entries: all, single: null,
      overShort: all.reduce((a, e) => a + e.overShort, 0),
      recon: all.reduce((a, e) => ({ orders: a.orders + (e.orders || 0), voidedLines: a.voidedLines + (e.recon?.voidedLines || 0), voidedAmount: a.voidedAmount + (e.recon?.voidedAmount || 0), failedOrders: a.failedOrders + (e.recon?.failedOrders || 0), failedAmount: a.failedAmount + (e.recon?.failedAmount || 0), refunds: a.refunds + (e.recon?.refunds || 0), giftCardLines: a.giftCardLines + (e.recon?.giftCardLines || 0), houseCharges: a.houseCharges + (e.recon?.houseCharges || 0) }),
        { orders: 0, voidedLines: 0, voidedAmount: 0, failedOrders: 0, failedAmount: 0, refunds: 0, giftCardLines: 0, houseCharges: 0 }),
      dispatch: (() => { const d = all.reduce((a, e) => e.dispatch ? { orders: a.orders + e.dispatch.orders, fee: a.fee + e.dispatch.fee, feeTax: a.feeTax + e.dispatch.feeTax, tip: a.tip + e.dispatch.tip } : a, { orders: 0, fee: 0, feeTax: 0, tip: 0 }); return d.orders ? d : null; })(),
    };
  }, [data, store, granularity, entryIdx]);

  const kpi = useMemo(() => {
    if (!view) return null;
    const by = (pred) => view.lines.filter(pred).reduce((a, l) => a + l.amount, 0);
    return {
      moneyIn: by(l => l.side === 'debit' && (l.kind === 'tender' || l.kind === 'ar')),
      sales: by(l => l.kind === 'sales' || l.kind === 'delivery'),
      taxes: by(l => /^tax[123]$/.test(l.kind)),
      tips: by(l => l.kind === 'tips'),
      giftCards: by(l => l.kind === 'giftcard'),
    };
  }, [view]);

  const unmapped = data?.unmappedTenders || [];
  const fmt = FORMATS.find(f => f.id === format) || FORMATS[2];
  const qbBlocked = fmt.qb && unmapped.length > 0;
  const allEntries = data ? flattenEntries(data) : [];

  const doExport = () => {
    const name = `sales-journal-${range.start}_${range.end}`;
    const text = fmt.id === 'iif' ? buildJournalIif(allEntries)
      : fmt.id === 'qbo' ? buildQboJournalCsv(allEntries)
        : fmt.id === 'summary' ? buildJournalSummaryCsv(allEntries)
          : buildJournalCsv(allEntries);
    downloadText(`${name}${fmt.id === 'qbo' ? '-qbo' : fmt.id === 'summary' ? '-summary' : ''}.${fmt.file}`, text, fmt.mime);
  };

  const storeName = (id) => locations.find(l => Number(l.ID) === Number(id))?.NAME || `Store ${id}`;

  return (
    <div className="grid" style={{ gap: 14 }}>
      <Card tour="sj-period">
        <div className="chip-row" style={{ alignItems: 'center', gap: 14 }}>
          <PeriodPicker cfg={{ type: 'monthly' }} value={range} onChange={setRange} maxDate={anchor} />
          <label className="hint" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            One entry per
            <select className="field" value={granularity} onChange={e => { granTouched.current = true; setGranularity(e.target.value); }}>
              <option value="month">store per month</option>
              <option value="day">store per business day</option>
            </select>
          </label>
          <span className="hint">{data?.basis || 'Paid orders by paid time, 4 AM business day'}</span>
        </div>
      </Card>

      {error && <ErrorBox message={error} />}
      {loading && !data && <Spinner label="Building the journal…" />}

      {data && stores.length === 0 && <Empty>No paid orders in this period for the selected stores.</Empty>}

      {data && stores.length > 0 && (
        <>
          {unmapped.length > 0 && (
            <div className="notice bad" data-tour="sj-unmapped">
              <strong>{unmapped.length} tender{unmapped.length > 1 ? 's' : ''} not mapped to an account:</strong>{' '}
              {unmapped.map(u => `${u.tender} (${u.stores.map(storeName).join(', ')})`).join(' · ')}.
              {' '}They are booked to the default account and the QuickBooks exports are held until they are mapped.{' '}
              <button className="btn-ghost" onClick={() => window.dispatchEvent(new CustomEvent('sd-navigate', { detail: 'admin' }))}>Map them in Accounting Profiles →</button>
            </div>
          )}
          {data.unattributed && (
            <div className="notice">
              <strong>Not booked to a store:</strong> this snapshot's {data.unattributed.housePayments.length ? 'house-account payments' : ''}{data.unattributed.housePayments.length && data.unattributed.costs.length ? ' and ' : ''}{data.unattributed.costs.length ? 'POS costs' : ''} carry no store id, so they are listed here rather than in a journal:{' '}
              {[...data.unattributed.housePayments.map(p => `${p.tender} payment ${money(p.amount)}`), ...data.unattributed.costs.map(c => `${c.name} ${money(c.amount)}`)].join(' · ')}.
            </div>
          )}

          {stores.length > 1 && (
            <div className="chip-row" data-tour="sj-stores">
              <button className={`chip ${storeId === 'all' ? 'active' : ''}`} onClick={() => setStoreId('all')}>All stores</button>
              {stores.map(s => (
                <button key={s.locationId} className={`chip ${String(storeId) === String(s.locationId) ? 'active' : ''}`} onClick={() => setStoreId(s.locationId)}>
                  {s.name}{s.entries.some(e => e.unmapped.length) ? ' ⚠' : ''}
                </button>
              ))}
            </div>
          )}

          {view && (
            <>
              <div className="grid kpi-grid" data-tour="sj-kpis">
                <KpiCard label="Money in" value={money(kpi.moneyIn)} sub="all tenders + house accounts" />
                <KpiCard label="Sales" value={money(kpi.sales)} sub="net of discounts, excl. gift cards" />
                <KpiCard label="Taxes collected" value={money(kpi.taxes)} sub={store ? Object.values(store.taxLabels || {}).filter(Boolean).join(' · ') : 'GST/HST · PST · LCT'} />
                <KpiCard label="Tips payable" value={money(kpi.tips)} sub="incl. split-payment tips" />
                <KpiCard label="Over / short" value={money(view.overShort)} sub={view.overShort === 0 ? 'balanced' : 'unexplained difference'} />
              </div>

              {store && granularity === 'day' && view.entries.length > 1 && (
                <div className="chip-row">
                  <span className="hint">Day:</span>
                  <select className="field" value={entryIdx} onChange={e => setEntryIdx(Number(e.target.value))}>
                    {view.entries.map((e, i) => <option key={e.period} value={i}>{e.periodLabel || e.period}</option>)}
                  </select>
                </div>
              )}

              <Ledger
                tour="sj-ledger"
                title={store ? `${store.name} — ${view.single?.periodLabel || `${view.entries.length} days`}` : `All stores — ${stores.length} journals`}
                sub={store
                  ? `journal ${view.single?.docNum || ''} · profile “${store.profile.name}”${store.profile.isDefault ? ' (built-in default)' : ''} · ${store.province}`
                  : 'every store’s journal added together; export writes one entry per store'}
                lines={view.lines}
                badge={<BalanceBadge overShort={view.overShort} />}
              />

              <ChannelCard entries={view.single ? [view.single] : view.entries} />

              {view.recon && (
                <Card title="Reconciliation" sub="counts to check against the POS — none of these move money in the journal" tour="sj-recon">
                  <div className="grid kpi-grid">
                    <KpiCard label="Paid orders" value={int(view.recon.orders)} />
                    <KpiCard label="Voided items" value={int(view.recon.voidedLines)} sub={money(view.recon.voidedAmount)} />
                    <KpiCard label="Failed / cancelled" value={int(view.recon.failedOrders)} sub={money(view.recon.failedAmount)} />
                    <KpiCard label="Refunds" value={int(view.recon.refunds)} sub="dated by the original sale" />
                    <KpiCard label="Gift cards sold" value={int(view.recon.giftCardLines)} sub={money(kpi.giftCards)} />
                    <KpiCard label="House-account charges" value={int(view.recon.houseCharges)} />
                  </div>
                  {view.dispatch && (
                    <div className="hint" style={{ marginTop: 10 }}>
                      <strong>Courier pass-through (not revenue):</strong> {int(view.dispatch.orders)} dispatch orders · courier fee {money(view.dispatch.fee)} · fee tax {money(view.dispatch.feeTax)} · courier tips {money(view.dispatch.tip)} — already removed from every line above.
                    </div>
                  )}
                </Card>
              )}
            </>
          )}

          {stores.length > 1 && (
            <Card title="By store" sub="one journal per store per period">
              <DataTable
                columns={[
                  { key: 'store', label: 'Store' },
                  { key: 'period', label: 'Period' },
                  { key: 'orders', label: 'Orders', num: true, render: r => int(r.orders) },
                  { key: 'debits', label: 'Money in', num: true, render: r => money(r.debits), csv: r => (r.debits / 100).toFixed(2) },
                  { key: 'netSales', label: 'Net sales', num: true, render: r => money(r.netSales), csv: r => (r.netSales / 100).toFixed(2) },
                  { key: 'overShort', label: 'Over/short', num: true, render: r => <span className={r.overShort ? 'pill warn' : 'pill good'}>{money(r.overShort)}</span>, csv: r => (r.overShort / 100).toFixed(2) },
                  { key: 'unmapped', label: 'Unmapped', render: r => r.unmapped.length ? <span className="pill bad">{r.unmapped.join(', ')}</span> : '' },
                ]}
                rows={allEntries.map(e => ({ ...e, period: e.periodLabel || e.period, __key: e.docNum }))}
                defaultSort={{ key: 'store', dir: 'asc' }}
              />
            </Card>
          )}

          <Card title="Export" sub="built from the journals above — every store in view, one entry per store per period" tour="sj-export">
            <div className="chip-row" style={{ alignItems: 'center' }}>
              <select className="field" value={fmt.id} onChange={e => setFormat(e.target.value)}>
                {FORMATS.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
              </select>
              <button className="btn-primary inline" disabled={qbBlocked || allEntries.length === 0} onClick={doExport}>
                ⬇ Download {allEntries.length} entr{allEntries.length === 1 ? 'y' : 'ies'}
              </button>
              {qbBlocked && <span className="pill bad">map the tenders above first</span>}
            </div>
            <div className="hint" style={{ marginTop: 8 }}>
              {fmt.id === 'iif' && 'Import in QuickBooks Desktop via File › Utilities › Import › IIF Files. Account names must already exist in your chart of accounts.'}
              {fmt.id === 'qbo' && 'Import in QuickBooks Online via Settings › Import data › Journal entries. Turn account numbers off first; Location/Class columns need those features enabled.'}
              {fmt.id === 'journal' && 'One row per journal line (Date, Journal No, Account, Debit, Credit, …) — maps into Xero, Sage or a spreadsheet.'}
              {fmt.id === 'summary' && 'One row per store per period with a column per tender and tax — for reconciliation in a spreadsheet.'}
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
